/**
 * OperationMovementsModal — histórico de movimentações de TODOS os títulos de uma operação.
 *
 * Difere do MovementsModal (que é por título) na coluna "Título": numa operação com várias
 * parcelas, sem ela não se sabe de onde veio cada movimentação. Mesmo formato de payload,
 * então o render das demais colunas é o mesmo.
 */
import { useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import {
    movementTypeLabels, businessReferenceLabels, businessReferenceLabelsPlural,
} from '../constants/settlement';
import { getOperationMovements } from '../services/settlementApi';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatDateBR } from '../../../../utils/DateUtils';

interface Props {
    operation: any;
    onClose: () => void;
}

const operationTypeLabels: Record<string, string> = {
    LOAN: 'Empréstimo',
    RECEIVABLES_ASSIGNMENT: 'Cessão de recebíveis',
    ACKNOWLEDGEMENT_OF_DEBT: 'Confissão de dívida',
    STRUCTURED_OPERATION: 'Operação estruturada',
};

const referenceLabel = (ref?: string) => businessReferenceLabelsPlural[ref || ''] || 'Títulos';

/** Cabeçalho da coluna: "Recebível" em cessão, "Parcela" em empréstimo. */
const referenceColumn = (ref?: string) => businessReferenceLabels[ref || ''] || 'Parcela';

const OperationMovementsModal: React.FC<Props> = ({ operation, onClose }) => {
    const [movements, setMovements] = useState<any[]>([]);
    const [titleDebtsCount, setTitleDebtsCount] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            try {
                const data = await getOperationMovements(operation.credit_operation_id);
                setMovements(data.data || []);
                setTitleDebtsCount(data.title_debts_count ?? null);
            } catch (e: any) {
                // 404 = operação faturada cujos títulos ainda não chegaram
                setError(String(e?.message).includes('404')
                    ? 'Esta operação ainda não possui títulos registrados.'
                    : 'Não foi possível carregar as movimentações.');
            }
            setLoading(false);
        })();
    }, [operation.credit_operation_id]);

    const titleLabel = (m: any) => {
        if (m.title_contract_number != null && m.title_amendment_number != null)
            return `${m.title_contract_number}-${m.title_amendment_number}`;
        if (m.title_contract_number != null) return String(m.title_contract_number);
        return m.title_document_title || (m.title_debt_id ? String(m.title_debt_id).slice(0, 8) : '-');
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Movimentações — {operation.contract_label || `${operation.contract_number || '?'}-${operation.amendment_number ?? 0}`}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <FaTimes size={18} />
                    </button>
                </div>

                <div className="mb-4 grid grid-cols-4 gap-4 text-sm bg-gray-50 dark:bg-slate-700 p-3 rounded">
                    <div><span className="text-gray-500 dark:text-gray-400">Tipo:</span> <strong>{operationTypeLabels[operation.operation_type] || operation.operation_type || '-'}</strong></div>
                    <div><span className="text-gray-500 dark:text-gray-400">Valor Nominal:</span> <strong>{formatBRL(operation.nominal_value)}</strong></div>
                    <div><span className="text-gray-500 dark:text-gray-400">$ Recebido:</span> <strong className="text-green-600">{formatBRL(operation.paid_amount)}</strong></div>
                    <div><span className="text-gray-500 dark:text-gray-400">{referenceLabel(operation.business_reference)}:</span> <strong>{titleDebtsCount ?? operation.title_debts_count ?? '-'}</strong></div>
                </div>

                {loading ? (
                    <p className="text-center text-gray-500 py-4">Carregando...</p>
                ) : error ? (
                    <p className="text-center text-gray-500 py-4">{error}</p>
                ) : movements.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">Nenhuma movimentação encontrada</p>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100 dark:bg-slate-700">
                            <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase">
                                <th className="px-3 py-2">#</th>
                                <th className="px-3 py-2">Título</th>
                                <th className="px-3 py-2 text-center">
                                    {referenceColumn(operation.business_reference)}
                                </th>
                                <th className="px-3 py-2">Tipo</th>
                                <th className="px-3 py-2 text-right">Valor</th>
                                <th className="px-3 py-2">Data</th>
                                <th className="px-3 py-2">Fonte</th>
                                <th className="px-3 py-2">Fatura</th>
                            </tr>
                        </thead>
                        <tbody>
                            {movements.map((m: any) => (
                                <tr key={m.id} className="border-t dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700">
                                    <td className="px-3 py-2 text-xs font-mono text-gray-500" title={m.charge_id || ''}>
                                        {m.charge_id ? String(m.charge_id).slice(0, 8) : '-'}
                                    </td>
                                    <td className="px-3 py-2 text-xs font-medium" title={m.title_debt_id || ''}>{titleLabel(m)}</td>
                                    <td className="px-3 py-2 text-center">{m.title_installment_number ?? '-'}</td>
                                    <td className="px-3 py-2">
                                        <span className={m.movement_type === 'REVERSAL' ? 'text-red-600' : ''}>
                                            {movementTypeLabels[m.movement_type] || m.movement_type}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right font-medium">{formatBRL(m.amount)}</td>
                                    <td className="px-3 py-2">{formatDateBR(m.movement_date)}</td>
                                    <td className="px-3 py-2">{m.payment_source || '-'}</td>
                                    <td className="px-3 py-2 text-xs font-mono text-gray-500" title={m.invoice_id || ''}>
                                        {m.invoice_id ? String(m.invoice_id).slice(0, 8) : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default OperationMovementsModal;
